import React, { useState, useCallback, useEffect, useRef } from 'react';
import Map from './Map';
import skiData from '../services/ski-data';
import './WeatherMap.css';
import ResortDetail from './ResortDetail';

const INDEX_TYPES = [
  { value: 'skiability', text: '适滑指数' },
  { value: 'temperature', text: '温度指数' },
  { value: 'wind', text: '风力指数' },
  { value: 'snow', text: '降雪指数' }
];

const WeatherMap = () => {
  console.log('WeatherMap component rendered');

  const [resorts, setResorts] = useState([]);
  const [selectedResort, setSelectedResort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [indexType, setIndexType] = useState('skiability');
  const mapRef = useRef(null);

  // 计算日期范围
  const getDateRange = () => {
    const today = new Date();
    const sixDaysLater = new Date(today);
    sixDaysLater.setDate(today.getDate() + 6); // 今天加上后面6天

    return {
      min: today.toISOString().split('T')[0],
      max: sixDaysLater.toISOString().split('T')[0]
    };
  };

  // 初始化日期状态
  const [dateRange] = useState(getDateRange());
  const [startDate, setStartDate] = useState(dateRange.min); // 默认从今天开始
  const [endDate, setEndDate] = useState(dateRange.max);    // 默认到6天后
  const [currentDate, setCurrentDate] = useState(dateRange.min);

  // 添加一个标记来追踪是否是用户手动移动地图
  const isUserMovingRef = useRef(false);

  // 将 convertToMapMarkers 的结果存储在 state 中
  const [mapMarkers, setMapMarkers] = useState([]);

  // 添加播放相关状态
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackInterval = useRef(null);

  // 修改播放速度选项为最小值和最大值
  const SPEED_RANGE = {
    min: 1,
    max: 20
  };

  // 获取下一天的日期
  const getNextDate = (date) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    return nextDate.toISOString().split('T')[0];
  };

  // 添加状态来控制 ResortDetail 的显示
  const [showResortDetail, setShowResortDetail] = useState(false);
  const [selectedResortDetail, setSelectedResortDetail] = useState(null);

  // 在组件挂载时执行一次
  useEffect(() => {
    console.log('WeatherMap component mounted');
  }, []);

  // 加载天气数据（不改变地图范围）
  const loadWeatherData = useCallback(async (currentDate) => {
    try {
      setLoading(true);
      
      // 只在地图已加载时获取边界
      let boundParams;
      if (mapRef.current) {
        const currentBounds = mapRef.current.getBounds();
        boundParams = {
          north: currentBounds.north,
          south: currentBounds.south,
          east: currentBounds.east,
          west: currentBounds.west
        };
      } else {
        // 使用默认边界值
        boundParams = {
          north: 50,
          south: 15,
          east: 135,
          west: 90
        };
      }
      
      const params = {
        startDate: startDate,
        endDate: endDate,
        indexType,
        ...boundParams
      };
      
      const response = await skiData.getWeatherMap(params);
      
      if (response.success && Array.isArray(response.resorts)) {
        const formattedResorts = response.resorts.map(resort => ({
          ...resort,
          weatherMultiplier: resort.skiability || resort.weatherMultiplier || 0,
          level: getWeatherLevel(
            indexType === 'snow' ? resort.snow :
            indexType === 'temperature' ? resort.temperature :
            indexType === 'wind' ? resort.wind :
            resort.skiability || resort.weatherMultiplier || 0,
            indexType
          ),
          maxTemp: resort.maxTemp || resort.temperature || 0,
          minTemp: resort.minTemp || resort.temperature || 0,
          condition: resort.condition || '未知',
          willSnow: !!resort.willSnow
        }));

        // 直接设置 resorts，不触发地图视角变化
        setResorts(formattedResorts);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load weather data:', error);
      setError('加载天气数据失败');
      setResorts([]);
    } finally {
      setLoading(false);
    }
  }, [indexType, startDate, endDate]);

  // 处理地图加载完成
  const handleMapLoad = useCallback(() => {
    loadWeatherData(currentDate);
  }, [loadWeatherData, currentDate]);

  // 处理地图移动
  const handleMapMove = useCallback(() => {
    // 只在用户手动移动地图时重新加载数据
    if (isUserMovingRef.current) {
      loadWeatherData(currentDate);
      isUserMovingRef.current = false;
    }
  }, [loadWeatherData, currentDate]);

  // 处理用户开始移动地图
  const handleMapDragStart = useCallback(() => {
    isUserMovingRef.current = true;
  }, []);

  // 根据天气适滑指数获取等级
  const getWeatherLevel = (value, type) => {
    if (type === 'snow') {
      // 降雪指数的特殊处理
      const snowLevel = (value - 5) * 2;
      if (snowLevel <= 0) return 0;    // 深红色 - 无降雪
      if (snowLevel <= 2) return 2;    // 红色 - 1cm
      if (snowLevel <= 4) return 4;    // 粉红色 - 2cm
      if (snowLevel <= 6) return 6;    // 黄色 - 5cm
      if (snowLevel <= 8) return 8;    // 浅蓝色 - 10cm
      return 10;                       // 绿色 - 20cm
    } else {
      // 其他指数（适滑指数、温度指数、风力指数）的处理
      if (value >= 10) return 10;      // 优秀
      if (value >= 8) return 8;        // 良好
      if (value >= 6) return 6;        // 一般
      if (value >= 4) return 4;        // 较差
      if (value >= 2) return 2;        // 很差
      return 0;                        // 不适合
    }
  };

  // 当 resorts 或其他相关依赖项变化时更新 mapMarkers
  useEffect(() => {
    const newMarkers = resorts.map(resort => {
      if (!resort || !resort.id) {
        console.warn('Invalid resort data:', resort);
        return null;
      }

      // 如果是室内滑雪场，使用固定的灰色标记
      if (resort.isIndoor) {
        return {
          id: resort.id,
          longitude: resort.longitude,
          latitude: resort.latitude,
          isSelected: selectedResort?.id === resort.id,
          skiability: -1, // 使用特殊值表示室内滑雪场
          isIndoor: true,
          markerColor: '#808080', // 明确指定标记颜色
          markerClass: 'indoor', // 添加特殊的 CSS 类
          resortData: resort,
          popup: `
            <div class="weather-popup">
              <h3>${resort.name}</h3>
              <p>${resort.country}${resort.region ? ` · ${resort.region}` : ''}</p>
              <p>室内滑雪场</p>
              <p>天气：${resort.condition || '未知'}</p>
              <p>温度：${resort.maxTemp || resort.temperature || 0}° / ${resort.minTemp || resort.temperature || 0}°</p>
            </div>
          `
        };
      }

      // 根据不同的指数类型获取对应的值
      let value;
      switch (indexType) {
        case 'skiability':
          value = resort.skiability;
          break;
        case 'temperature':
          value = resort.temperature;
          break;
        case 'wind':
          value = resort.wind;
          break;
        case 'snow':
          value = resort.snow;
          console.log('Resort snow value:', resort.name, value);
          break;
        default:
          value = 0;
      }

      // 获取标记等级
      const level = getWeatherLevel(value, indexType);
      console.log('Calculated level:', resort.name, level);

      // 格式化显示值
      let displayValue;
      if (indexType === 'snow') {
        const snowLevel = (value - 5) * 2;
        if (snowLevel <= 0) displayValue = '无降雪';
        else if (snowLevel <= 2) displayValue = '1cm';
        else if (snowLevel <= 4) displayValue = '2cm';
        else if (snowLevel <= 6) displayValue = '5cm';
        else if (snowLevel <= 8) displayValue = '10cm';
        else displayValue = '20cm';
      } else {
        displayValue = Number(value).toFixed(1);
      }

      return {
        id: resort.id,
        longitude: resort.longitude,
        latitude: resort.latitude,
        isSelected: selectedResort?.id === resort.id,
        skiability: value,
        level: level, // 使用新的 level 值
        resortData: resort,
        popup: `
          <div class="weather-popup">
            <h3>${resort.name}</h3>
            <p>${resort.country}${resort.region ? ` · ${resort.region}` : ''}</p>
            <p>${INDEX_TYPES.find(t => t.value === indexType)?.text}：${displayValue}</p>
            <p>天气：${resort.condition || '未知'}</p>
            <p>温度：${resort.maxTemp || resort.temperature || 0}° / ${resort.minTemp || resort.temperature || 0}°</p>
            ${resort.willSnow ? '<p class="snow-tag">今日降雪</p>' : ''}
          </div>
        `
      };
    }).filter(Boolean); // 过滤掉无效的标记
    
    setMapMarkers(newMarkers);
  }, [resorts, selectedResort, indexType]);

  // 处理日期范围变化
  const handleDateRangeChange = async (type, newDate) => {
    if (new Date(newDate) < new Date(dateRange.min) || 
        new Date(newDate) > new Date(dateRange.max)) {
      return;
    }

    let updatedStartDate = startDate;
    let updatedEndDate = endDate;
    let updatedCurrentDate = currentDate;

    if (type === 'start') {
      if (new Date(newDate) > new Date(endDate)) {
        updatedEndDate = newDate;
      }
      updatedStartDate = newDate;
      updatedCurrentDate = newDate;
    } else {
      if (new Date(newDate) < new Date(startDate)) {
        updatedStartDate = newDate;
        updatedCurrentDate = newDate;
      }
      updatedEndDate = newDate;
    }
    
    // 先加载新数据
    try {
      setLoading(true);
      let boundParams;
      if (mapRef.current) {
        const currentBounds = mapRef.current.getBounds();
        boundParams = {
          north: currentBounds.north,
          south: currentBounds.south,
          east: currentBounds.east,
          west: currentBounds.west
        };
      } else {
        boundParams = {
          north: 50,
          south: 15,
          east: 135,
          west: 90
        };
      }
      
      const params = {
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        indexType,
        ...boundParams
      };
      
      const response = await skiData.getWeatherMap(params);
      
      if (response.success && Array.isArray(response.resorts)) {
        const formattedResorts = response.resorts.map(resort => ({
          ...resort,
          weatherMultiplier: resort.skiability || resort.weatherMultiplier || 0,
          level: getWeatherLevel(
            indexType === 'snow' ? resort.snow :
            indexType === 'temperature' ? resort.temperature :
            indexType === 'wind' ? resort.wind :
            resort.skiability || resort.weatherMultiplier || 0,
            indexType
          ),
          maxTemp: resort.maxTemp || resort.temperature || 0,
          minTemp: resort.minTemp || resort.temperature || 0,
          condition: resort.condition || '未知',
          willSnow: !!resort.willSnow
        }));

        // 数据加载成功后，更新日期状态和 resorts
        setStartDate(updatedStartDate);
        setEndDate(updatedEndDate);
        setCurrentDate(updatedCurrentDate);
        setResorts(formattedResorts);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load weather data:', error);
      setError('加载天气数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 添加处理天气指数变化的函数
  const handleIndexTypeChange = async (newIndexType) => {
    try {
      setLoading(true);
      let boundParams;
      if (mapRef.current) {
        const currentBounds = mapRef.current.getBounds();
        boundParams = {
          north: currentBounds.north,
          south: currentBounds.south,
          east: currentBounds.east,
          west: currentBounds.west
        };
      } else {
        boundParams = {
          north: 50,
          south: 15,
          east: 135,
          west: 90
        };
      }
      
      const params = {
        startDate,
        endDate,
        indexType: newIndexType,
        ...boundParams
      };
      
      const response = await skiData.getWeatherMap(params);
      
      if (response.success && Array.isArray(response.resorts)) {
        const formattedResorts = response.resorts.map(resort => ({
          ...resort,
          weatherMultiplier: resort.skiability || resort.weatherMultiplier || 0,
          level: getWeatherLevel(
            newIndexType === 'snow' ? resort.snow :
            newIndexType === 'temperature' ? resort.temperature :
            newIndexType === 'wind' ? resort.wind :
            resort.skiability || resort.weatherMultiplier || 0,
            newIndexType
          ),
          maxTemp: resort.maxTemp || resort.temperature || 0,
          minTemp: resort.minTemp || resort.temperature || 0,
          condition: resort.condition || '未知',
          willSnow: !!resort.willSnow
        }));

        // 数据加载成功后，更新指数类型和 resorts
        setIndexType(newIndexType);
        setResorts(formattedResorts);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load weather data:', error);
      setError('加载天气数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理单天数据加载
  const loadSingleDayData = async (date) => {
    try {
      setLoading(true);
      let boundParams;
      if (mapRef.current) {
        const currentBounds = mapRef.current.getBounds();
        boundParams = {
          north: currentBounds.north,
          south: currentBounds.south,
          east: currentBounds.east,
          west: currentBounds.west
        };
      } else {
        boundParams = {
          north: 50,
          south: 15,
          east: 135,
          west: 90
        };
      }
      
      const params = {
        startDate: date,
        endDate: date,
        indexType,
        ...boundParams
      };
      
      const response = await skiData.getWeatherMap(params);
      
      if (response.success && Array.isArray(response.resorts)) {
        const formattedResorts = response.resorts.map(resort => ({
          ...resort,
          weatherMultiplier: resort.skiability || resort.weatherMultiplier || 0,
          level: getWeatherLevel(
            indexType === 'snow' ? resort.snow :
            indexType === 'temperature' ? resort.temperature :
            indexType === 'wind' ? resort.wind :
            resort.skiability || resort.weatherMultiplier || 0,
            indexType
          ),
          maxTemp: resort.maxTemp || resort.temperature || 0,
          minTemp: resort.minTemp || resort.temperature || 0,
          condition: resort.condition || '未知',
          willSnow: !!resort.willSnow
        }));

        setStartDate(date);
        setEndDate(date);
        setCurrentDate(date);
        setResorts(formattedResorts);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Failed to load weather data:', error);
      setError('加载天气数据失败');
      stopPlayback();
    } finally {
      setLoading(false);
    }
  };

  // 开始播放
  const startPlayback = () => {
    setIsPlaying(true);
    const intervalTime = 10000 / playbackSpeed; // 1倍速下10秒切换一次

    playbackInterval.current = setInterval(() => {
      setCurrentDate(prevDate => {
        const nextDate = getNextDate(prevDate);
        // 如果超过结束日期，回到开始日期
        if (new Date(nextDate) > new Date(dateRange.max)) {
          loadSingleDayData(dateRange.min);
          return dateRange.min;
        } else {
          loadSingleDayData(nextDate);
          return nextDate;
        }
      });
    }, intervalTime);
  };

  // 停止播放
  const stopPlayback = () => {
    setIsPlaying(false);
    if (playbackInterval.current) {
      clearInterval(playbackInterval.current);
      playbackInterval.current = null;
    }
  };

  // 处理播放速度变化
  const handleSpeedChange = (newSpeed) => {
    setPlaybackSpeed(newSpeed);
    if (isPlaying) {
      stopPlayback();
      startPlayback();
    }
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (playbackInterval.current) {
        clearInterval(playbackInterval.current);
      }
    };
  }, []);

  // 修改处理选择滑雪场的函数
  const handleResortSelect = async (resort) => {
    // 添加空值检查
    if (!resort) {
      console.warn('No resort data provided');
      return;
    }

    console.log('Selected resort:', resort);
    setSelectedResort(resort);
    
    try {
      // 获取滑雪场详细信息
      const response = await skiData.getResortDetail(resort.id);
      console.log('Resort detail response:', response);

      if (response && !response.error) {
        // API 返回的数据已经是我们需要的格式，不需要再访问 .resort
        setSelectedResortDetail(response);
        setShowResortDetail(true);
      } else {
        console.error('Invalid resort detail response:', response);
        // 如果 API 响应无效，使用基本数据
        setSelectedResortDetail(resort);
        setShowResortDetail(true);
      }
    } catch (error) {
      console.error('Failed to load resort detail:', error);
      // 如果 API 请求失败，使用基本数据
      setSelectedResortDetail(resort);
      setShowResortDetail(true);
    }
  };

  // 处理关闭 ResortDetail
  const handleCloseDetail = () => {
    setShowResortDetail(false);
    setSelectedResort(null);
    setSelectedResortDetail(null);
  };

  // 修改 formatIndexValue 函数来正确显示值
  const formatIndexValue = (value, type) => {
    if (!value && value !== 0) return '暂无数据';
    
    switch (type) {
      case 'temperature':
        return `${value.toFixed(1)}°C`;
      case 'wind':
        return `${value.toFixed(1)}km/h`;
      case 'snow':
        const snowLevel = (value - 5) * 2;
        if (snowLevel <= 0) return '无降雪';
        else if (snowLevel <= 2) return '1cm';
        else if (snowLevel <= 4) return '2cm';
        else if (snowLevel <= 6) return '5cm';
        else if (snowLevel <= 8) return '10cm';
        else return '20cm';
      default:
        return value.toFixed(1);
    }
  };

  return (
    <div className="weather-map-container">
      {/* 控制面板 */}
      <div className="weather-controls">
        <div className="control-section">
          <h3>天气指数</h3>
          <select 
            className="index-select"
            value={indexType}
            onChange={(e) => handleIndexTypeChange(e.target.value)}
          >
            {INDEX_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.text}
              </option>
            ))}
          </select>
        </div>

        <div className="control-section">
          <h3>日期范围</h3>
          <div className="date-range">
            <input
              type="date"
              value={startDate}
              min={dateRange.min}
              max={dateRange.max}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="date-select"
            />
            <span className="date-separator">至</span>
            <input
              type="date"
              value={endDate}
              min={dateRange.min}
              max={dateRange.max}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="date-select"
            />
          </div>
        </div>

        {/* 播放控制 */}
        <div className="control-section">
          <h3>播放控制</h3>
          <div className="playback-controls">
            <button 
              className={`play-button ${isPlaying ? 'playing' : ''}`}
              onClick={() => isPlaying ? stopPlayback() : startPlayback()}
            >
              {isPlaying ? '暂停' : '播放'}
            </button>
            <div className="speed-controls">
              <span>播放速度: {playbackSpeed}x</span>
              <input
                type="range"
                min={SPEED_RANGE.min}
                max={SPEED_RANGE.max}
                value={playbackSpeed}
                onChange={(e) => handleSpeedChange(Number(e.target.value))}
                className="speed-slider"
              />
            </div>
          </div>
        </div>
      </div>

      {/* 地图容器 */}
      <div className="map-container">
        <Map
          ref={mapRef}
          markers={mapMarkers}
          selectedResort={selectedResort}
          onResortSelect={handleResortSelect}
          onMapMove={handleMapMove}
          onMapLoad={handleMapLoad}
          onMapDragStart={handleMapDragStart}
          type="weather"
        />
      </div>

      {/* 添加 ResortDetail 组件 */}
      {showResortDetail && selectedResortDetail && (
        <div className="resort-detail-overlay">
          <ResortDetail
            resort={selectedResortDetail}
            onClose={handleCloseDetail}
          />
        </div>
      )}

      {/* 图例 */}
      <div className="weather-legend">
        <h3>{INDEX_TYPES.find(t => t.value === indexType)?.text}</h3>
        {indexType === 'snow' ? (
          <>
            <div className="legend-item">
              <span className="legend-dot level-10"></span>
              <span>20cm</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-8"></span>
              <span>10cm</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-6"></span>
              <span>5cm</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-4"></span>
              <span>2cm</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-2"></span>
              <span>1cm</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-0"></span>
              <span>无降雪</span>
            </div>
          </>
        ) : (
          <>
            <div className="legend-item">
              <span className="legend-dot level-10"></span>
              <span>优秀 (≥10)</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-8"></span>
              <span>良好 (8-9)</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-6"></span>
              <span>一般 (6-7)</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-4"></span>
              <span>较差 (4-5)</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-2"></span>
              <span>很差 (2-3)</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot level-0"></span>
              <span>不适合 (0-1)</span>
            </div>
          </>
        )}
        <div className="legend-item">
          <span className="legend-dot indoor"></span>
          <span>室内滑雪场</span>
        </div>
      </div>

      {/* 加载状态 */}
      {loading && <div className="loading">加载中...</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
};

export default WeatherMap; 